import { axios, ENDPOINT } from "./constants"
import {
  adjustDefaultParams,
  getDataAndCountFromResponse,
  getQueryForAllAndCount,
  handleSilentApiError,
} from "./utils"

const ROI_DOCUMENT_QUERY = `
idRoiDetailDocument
documentName
`

const ROI_DETAIL_QUERY = `
  idReceiptOfInvoiceDetail
  totalAmount
  invoice {
    idInvoice
    invoiceNumber
    date
    currency {
      code
    }
  }
  documents {
    ${ROI_DOCUMENT_QUERY}
  }
`
const ROI_QUERY = `
idReceiptOfInvoice
createdAt
updatedAt
customer {
  idCust
  name
  custCode
}
creator
user {
  idUser
  name
}
totalAmountSum
details {
  ${ROI_DETAIL_QUERY}
}
`

export default class ApiReceiptOfInvoice {
  static async getItem(id) {
    const query = `
    query {
      getReceiptOfInvoiceImpl(id: ${parseInt(id, 10)}) {
        ${ROI_QUERY}
      }
    }`

    const response = await axios.post(ENDPOINT.QUERY, { query })
    handleSilentApiError(response)

    return { data: response.data.data.getReceiptOfInvoiceImpl }
  }

  static async get(params = {}) {
    // simpan original params (buat FE filter nanti)
    const adjusted = adjustDefaultParams(params)

    let query = `
    query {
      allReceiptOfInvoiceImpl {
        ${ROI_QUERY}
      }
    }`

    query = getQueryForAllAndCount(query, adjusted)

    const response = await axios.post(ENDPOINT.QUERY, { query })
    handleSilentApiError(response)

    const { data } = getDataAndCountFromResponse(response)

    let mappedData = []
    ;(data || []).forEach((roi) => {
      const details = roi.details || []

      details.forEach((detail) => {
        const docs = detail.documents || []
        const hasDoc = (name) =>
          docs.some(
            (d) =>
              d.documentName &&
              d.documentName.toLowerCase() === name.toLowerCase()
          )

        mappedData.push({
          idReceiptOfInvoice: roi.idReceiptOfInvoice,
          idReceiptOfInvoiceDetail: detail.idReceiptOfInvoiceDetail,

          invoiceNumber: detail.invoice?.invoiceNumber || "",
          invoiceDate: detail.invoice?.date || null,
          currency: detail.invoice?.currency?.code || "IDR",
          totalAmount: Number(detail.totalAmount ?? 0),

          fpOriginal: hasDoc("FP Asli"),
          fpCopy: hasDoc("FP Copy"),
          invoiceOriginal: hasDoc("Invoice Asli"),
          invoiceCopy: hasDoc("Invoice Copy"),
          grnOriginal: hasDoc("GRN Asli"),
          doOriginal: hasDoc("DO Asli"),
          poCopy: hasDoc("PO Copy"),
        })
      })
    })

    // eslint-disable-next-line prefer-destructuring
    const searchText = params.searchText
    if (searchText) {
      const s = String(searchText).toLowerCase()

      mappedData = mappedData.filter((row) => {
        const invNum = (row.invoiceNumber || "").toLowerCase()
        const dateStr = row.invoiceDate ? String(row.invoiceDate) : ""

        return invNum.includes(s) || dateStr.includes(s)
      })
    }

    return { data: mappedData, count: mappedData.length }
  }

  static async create(payload) {
    const response = await axios.post(ENDPOINT.CREATE, {
      query: `
      mutation (
        $userId: Int!,
        $customerId: Int!,
        $detailInvoiceIds: [Int],
        $detailAmounts: [Decimal],
        $detailDocumentNames: [[String]],
        $creator: String
      ) {
        createReceiptOfInvoiceImpl(
          userId: $userId,
          customerId: $customerId,
          detailInvoiceIds: $detailInvoiceIds,
          detailAmounts: $detailAmounts
          detailDocuments: $detailDocumentNames,
          creator: $creator
        ) {
          ok
          roiId
        }
      }`,
      variables: {
        userId: payload.userId,
        customerId: payload.customerId,
        detailInvoiceIds: payload.detailInvoiceIds ?? [],
        detailAmounts: payload.detailAmounts ?? [],
        detailDocumentNames: payload.detailDocumentNames ?? [],
        creator: payload.creator,
      },
    })

    handleSilentApiError(response)
    return { data: response.data.data.createReceiptOfInvoiceImpl }
  }

  static async update(id, payload = {}) {
    const response = await axios.post(ENDPOINT.UPDATE, {
      query: `
      mutation (
        $id: Int!,
        $userId: Int,
        $customerId: Int,
        $detailInvoiceIds: [Int],
        $detailAmounts: [Decimal],
        $detailDocumentNames: [[String]],
        $creator: String
      ) {
        updateReceiptOfInvoiceImpl(
          id: $id,
          userId: $userId,
          customerId: $customerId,
          detailInvoiceIds: $detailInvoiceIds,
          detailAmounts: $detailAmounts,
          detailDocuments: $detailDocumentNames,
          creator: $creator
        ) {
          ok
        }
      }`,
      variables: {
        id,
        userId: payload.userId,
        customerId: payload.customerId,
        detailInvoiceIds: payload.detailInvoiceIds ?? [],
        detailAmounts: payload.detailAmounts ?? [],
        detailDocumentNames: payload.detailDocumentNames ?? [],
        creator: payload.creator,
      },
    })

    handleSilentApiError(response)
    return { data: response.data.data.updateReceiptOfInvoiceImpl }
  }

  static async delete(id) {
    const response = await axios.post(ENDPOINT.DELETE, {
      query: `
      mutation {
        deleteReceiptOfInvoiceImpl(id: ${id}) {
          ok
        }
      }`,
    })

    handleSilentApiError(response)
    return { data: response.data.data.deleteReceiptOfInvoiceImpl }
  }

  static async addDetail(payload) {
    /**
     * payload = { receiptOfInvoiceId, invoiceId, amount? }
     */
    const response = await axios.post(ENDPOINT.CREATE, {
      query: `
      mutation ($roiId: Int!, $invoiceId: Int!, $amount: Decimal) {
        addRoiDetailImpl(
          receiptOfInvoiceId: $roiId,
          invoiceId: $invoiceId,
          totalAmount: $amount
        ) {
          ok
          roiDetailId
        }
      }`,
      variables: {
        roiId: payload.receiptOfInvoiceId,
        invoiceId: payload.invoiceId,
        amount: payload.amount ?? null,
      },
    })

    handleSilentApiError(response)
    return { data: response.data.data.addRoiDetailImpl }
  }

  /** ADD document to detail */
  static async addDocument(payload) {
    /**
     * payload = { receiptOfInvoiceDetailId, documentName }
     */
    const response = await axios.post(ENDPOINT.CREATE, {
      query: `
      mutation ($detailId: Int!, $name: String!) {
        addRoiDocumentImpl(
          receiptOfInvoiceDetailId: $detailId,
          documentName: $name
        ) {
          ok
          roiDocumentId
        }
      }`,
      variables: {
        detailId: payload.receiptOfInvoiceDetailId,
        name: payload.documentName,
      },
    })

    handleSilentApiError(response)
    return { data: response.data.data.addRoiDocumentImpl }
  }

  /** DELETE detail row */
  static async deleteDetail(id) {
    const response = await axios.post(ENDPOINT.DELETE, {
      query: `
      mutation {
        deleteRoiDetailImpl(id: ${id}) { ok }
      }`,
    })

    handleSilentApiError(response)
    return { data: response.data.data.deleteRoiDetailImpl }
  }

  /** DELETE detail document */
  static async deleteDocument(id) {
    const response = await axios.post(ENDPOINT.DELETE, {
      query: `
      mutation {
        deleteRoiDocumentImpl(id: ${id}) { ok }
      }`,
    })

    handleSilentApiError(response)
    return { data: response.data.data.deleteRoiDocumentImpl }
  }
}
